import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import type { PmoDelaySeries, PmoDelayQuarter } from '../../../types/pmo-delay.js';

/** チャート高さ（Tailwind の h-72 と一致させる）。 @graph-connects none */
const CHART_HEIGHT_CLASS = 'h-72';

/** 実績線の色（PMO ビューのアクセントである琥珀）。 @graph-connects none */
const ACTUAL_COLOR = '#E8743B';
/** 先 Q 実績ペース線の色（比較用の中立グレー）。 @graph-connects none */
const PREV_QUARTER_COLOR = '#9AA0A6';
/** 目標線の色（達成基準なのでティール）。 @graph-connects none */
const TARGET_COLOR = '#1B9E9E';

export interface DelayTrendCardProps {
  /** 描画する区分のシリーズ */
  series: PmoDelaySeries;
  /** 当四半期（見出しの Q ラベルと期間表示に使う） */
  currentQuarter: PmoDelayQuarter;
  /** 先四半期（ペース線の由来を注記するのに使う） */
  previousQuarter: PmoDelayQuarter;
  /** 実績線の as-of */
  asOfDate: string;
}

/**
 * X 軸のラベル。日次 92 点すべてに日付を出すと潰れるので月初と 15 日だけ出す。
 * @graph-connects none
 */
function formatXAxisTick(date: string): string {
  const day = date.slice(8, 10);
  if (day !== '01' && day !== '15') return '';
  return `${String(Number(date.slice(5, 7)))}/${String(Number(day))}`;
}

/**
 * 実績と目標の差分バッジ。遅延日数は少ないほど良いので、負（目標より少ない）を良い側として出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO遅延カードの実績vs目標差分バッジ。目標以内なら成功色、超過なら警告色で日数差を表示する
 * @graph-connects Badge [calls] 差分バッジ描画
 */
function DiffBadge({ series }: { series: PmoDelaySeries }) {
  const { t } = useTranslation();
  const { diffDays, isWithinTarget } = series.summary;
  // 「目標に対して何日多い / 少ない」を符号なしの絶対値 + 文言で読ませる
  const amount = Math.abs(diffDays).toFixed(1);

  return (
    <Badge variant={isWithinTarget ? 'success' : 'warning'}>
      {isWithinTarget
        ? t('kpi.pmoDelivery.diff.under', { days: amount })
        : t('kpi.pmoDelivery.diff.over', { days: amount })}
    </Badge>
  );
}

/**
 * 現時点の実績 / 目標 / 先 Q ペースを並べる数値タイル。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMO遅延カードの数値タイル（ラベルと日数）を描画する
 * @graph-connects none
 */
function DelayStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
      <p className="text-xs text-[var(--color-text-muted)]">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold tabular-nums text-[var(--color-text-primary)]">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-[var(--color-text-muted)]">{hint}</p>}
    </div>
  );
}

/**
 * 遅延評価 1 区分（引き直し / 遅延）の Q 内累積推移カード。
 *
 * 実績・先 Q 実績ペース線・目標線を同じ日付軸に重ねる。基準線 2 本は営業日にだけ上がる
 * 階段線なので、土日祝は横ばいになって実績線と歩調が揃う。実績線は as-of で止める
 * （先の日付まで 0 が伸びると「まだ発生していない」と「まだ来ていない」が区別できない）。
 *
 * 遅延日数は少ないほど良い指標なので、実績線が基準線より下にあれば改善している。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO遅延の区分別Q内累積推移カード。累積遅延日数の実績線に先Q実績ペース線とその90%の目標線を重ね、現時点の実績・目標・先Qペース・差分の4タイルを表示する
 * @graph-connects DiffBadge [calls] 実績vs目標差分バッジ描画
 * @graph-connects DelayStat [calls] 数値タイル描画
 * @graph-connects LineChart [calls] Q内累積折れ線描画
 * @graph-connects Card [calls] カードコンポーネント描画
 */
export function DelayTrendCard({
  series,
  currentQuarter,
  previousQuarter,
  asOfDate,
}: DelayTrendCardProps) {
  const { t } = useTranslation();
  const categoryKey = `kpi.pmoDelivery.category.${series.category}`;
  const quarterLabel = t('kpi.pmoDelivery.quarterLabel', {
    fiscalYear: currentQuarter.fiscalYear,
    quarter: currentQuarter.quarter,
  });
  const previousQuarterLabel = t('kpi.pmoDelivery.quarterLabel', {
    fiscalYear: previousQuarter.fiscalYear,
    quarter: previousQuarter.quarter,
  });

  return (
    <Card className="flex h-full flex-col animate-content-reveal">
      <CardHeader
        title={`${t(`${categoryKey}.title`)}（${quarterLabel}）`}
        action={<DiffBadge series={series} />}
      />
      <CardContent className="flex flex-1 flex-col">
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <DelayStat
            label={t('kpi.pmoDelivery.stat.actual')}
            value={t('kpi.pmoDelivery.unit.days', { days: series.summary.actualDays.toFixed(1) })}
            hint={t('kpi.pmoDelivery.stat.actualHint', { count: series.summary.count })}
          />
          <DelayStat
            label={t('kpi.pmoDelivery.stat.target')}
            value={t('kpi.pmoDelivery.unit.days', { days: series.summary.targetDays.toFixed(1) })}
            hint={t('kpi.pmoDelivery.stat.targetHint')}
          />
          <DelayStat
            label={t('kpi.pmoDelivery.stat.previousQuarter')}
            value={t('kpi.pmoDelivery.unit.days', {
              days: series.summary.previousQuarterDays.toFixed(1),
            })}
          />
          <DelayStat
            label={t('kpi.pmoDelivery.stat.diff')}
            value={t('kpi.pmoDelivery.unit.days', {
              days: `${series.summary.diffDays > 0 ? '+' : ''}${series.summary.diffDays.toFixed(1)}`,
            })}
          />
        </div>

        <div className={CHART_HEIGHT_CLASS}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series.points} margin={{ top: 8, right: 16, left: 8, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="date"
                stroke="var(--color-text-muted)"
                fontSize={11}
                interval={0}
                tickFormatter={formatXAxisTick}
              />
              <YAxis stroke="var(--color-text-muted)" fontSize={11} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-bg-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: 'var(--color-text-primary)' }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                type="monotone"
                dataKey="actualDays"
                name={t('kpi.pmoDelivery.legend.actual')}
                stroke={ACTUAL_COLOR}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="previousQuarterDays"
                name={t('kpi.pmoDelivery.legend.previousQuarter', {
                  quarter: previousQuarterLabel,
                })}
                stroke={PREV_QUARTER_COLOR}
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="targetDays"
                name={t('kpi.pmoDelivery.legend.target')}
                stroke={TARGET_COLOR}
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <p className="mt-3 text-xs text-[var(--color-text-muted)]">
          {t('kpi.pmoDelivery.asOfNote', { date: asOfDate })}
        </p>
      </CardContent>
    </Card>
  );
}
